import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import Layout from "@/components/Layout";
import { 
  Lock,
  Bell,
  Globe,
  Shield,
  Trash2,
  AlertTriangle,
  Save
} from "lucide-react";

const ProfileSettings = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState({
    // 通知设置
    emailNotifications: true,
    smsNotifications: false,
    communityNotifications: true,

    // 隐私设置
    profilePublic: true,
    showEmail: false,
    showPhone: false
  });

  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (!token) {
      navigate('/');
      return;
    }
    
    setIsLoading(false);
  }, [navigate]);

  const handleSettingChange = (key: string, value: boolean) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSavePassword = () => {
    // TODO: 实现修改密码逻辑
    console.log('修改密码:', passwordForm);
  };

  const handleDeleteAccount = () => {
    if (window.confirm('确定要删除账户吗？此操作不可恢复！')) {
      // TODO: 实现删除账户逻辑
      console.log('删除账户');
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">加载中...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="bg-gradient-to-br from-blue-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            账户设置
          </h1>
          <p className="text-xl text-gray-600">
            管理您的账户安全和偏好设置
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {/* 密码设置 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lock className="h-5 w-5" />
                <span>密码设置</span>
              </CardTitle>
              <CardDescription>修改您的登录密码</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="oldPassword">当前密码</Label>
                <Input
                  id="oldPassword"
                  type="password"
                  value={passwordForm.oldPassword}
                  onChange={(e) => handlePasswordChange("oldPassword", e.target.value)}
                  placeholder="请输入当前密码"
                />
              </div>
              <div>
                <Label htmlFor="newPassword">新密码</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => handlePasswordChange("newPassword", e.target.value)}
                  placeholder="请输入新密码"
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword">确认新密码</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => handlePasswordChange("confirmPassword", e.target.value)}
                  placeholder="请再次输入新密码"
                />
              </div>
              <Button onClick={handleSavePassword}>
                <Save className="mr-2 h-4 w-4" />
                保存密码
              </Button>
            </CardContent>
          </Card>

          {/* 通知设置 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bell className="h-5 w-5" />
                <span>通知设置</span>
              </CardTitle>
              <CardDescription>管理您接收通知的方式</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">邮件通知</p>
                  <p className="text-sm text-gray-500">接收重要更新和活动通知</p>
                </div>
                <Switch
                  checked={settings.emailNotifications}
                  onCheckedChange={(checked) => handleSettingChange("emailNotifications", checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">短信通知</p>
                  <p className="text-sm text-gray-500">接收验证码和重要提醒</p>
                </div>
                <Switch
                  checked={settings.smsNotifications}
                  onCheckedChange={(checked) => handleSettingChange("smsNotifications", checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">社区通知</p>
                  <p className="text-sm text-gray-500">接收话题回复和点赞通知</p>
                </div>
                <Switch
                  checked={settings.communityNotifications}
                  onCheckedChange={(checked) => handleSettingChange("communityNotifications", checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* 隐私设置 */}
          {/*<Card>*/}
          {/*  <CardHeader>*/}
          {/*    <CardTitle className="flex items-center space-x-2">*/}
          {/*      <Shield className="h-5 w-5" />*/}
          {/*      <span>隐私设置</span>*/}
          {/*    </CardTitle>*/}
          {/*    <CardDescription>控制您的个人信息可见性</CardDescription>*/}
          {/*  </CardHeader>*/}
          {/*  <CardContent className="space-y-4">*/}
          {/*    <div className="flex items-center justify-between">*/}
          {/*      <div>*/}
          {/*        <p className="font-medium">公开个人资料</p>*/}
          {/*        <p className="text-sm text-gray-500">允许其他用户查看您的个人资料</p>*/}
          {/*      </div>*/}
          {/*      <Switch*/}
          {/*        checked={settings.profilePublic}*/}
          {/*        onCheckedChange={(checked) => handleSettingChange("profilePublic", checked)}*/}
          {/*      />*/}
          {/*    </div>*/}
          {/*    <div className="flex items-center justify-between">*/}
          {/*      <div>*/}
          {/*        <p className="font-medium">显示邮箱</p>*/}
          {/*        <p className="text-sm text-gray-500">在个人资料中显示邮箱地址</p>*/}
          {/*      </div>*/}
          {/*      <Switch*/}
          {/*        checked={settings.showEmail}*/}
          {/*        onCheckedChange={(checked) => handleSettingChange("showEmail", checked)}*/}
          {/*      />*/}
          {/*    </div>*/}
          {/*    <div className="flex items-center justify-between">*/}
          {/*      <div>*/}
          {/*        <p className="font-medium">显示手机号</p>*/}
          {/*        <p className="text-sm text-gray-500">在个人资料中显示手机号码</p>*/}
          {/*      </div>*/}
          {/*      <Switch*/}
          {/*        checked={settings.showPhone}*/}
          {/*        onCheckedChange={(checked) => handleSettingChange("showPhone", checked)}*/}
          {/*      />*/}
          {/*    </div>*/}
          {/*  </CardContent>*/}
          {/*</Card>*/}

          {/* 危险操作 */}
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                <span>危险操作</span>
              </CardTitle>
              <CardDescription>这些操作不可恢复，请谨慎操作</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="destructive" onClick={handleDeleteAccount}>
                <Trash2 className="mr-2 h-4 w-4" />
                删除账户
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
};

export default ProfileSettings;
